import {
  PropertyColumns,
  CompanyColumns,
  ContactColumns,
  LeaseColumns,
  TimColumns,
  AvailabilityColumns,
  SaleColumns,
  PortfoliosaleColumns,
} from '@miq/catalog';
import type { CompoundFilterConfig, CompoundFilterGroupDef, CompoundFilterGroupItem } from '@cbre/components';

const COMPANY_GROUP_LABEL = 'Company Level';

/**
 * Static customizations for the compound filter "Search on…" checkbox tree, keyed by the
 * parent filter's catalog key.
 *
 * These are translated from the pre-existing `develop`-branch configuration
 * (see `docs/old-compound-filters.ts`) into this implementation's shape. Two semantic changes
 * were made deliberately during translation — do not "fix" these back to match the old file:
 *
 * 1. Only `key` is set on each group item here (no `label`). Labels are backfilled at render
 *    time from the live BE catalog (`FilterOptionComponent.hydrateStaticGroups`), so they can
 *    never go stale relative to a hardcoded string here. An item can still set an explicit
 *    `label` to override the BE value if ever needed.
 * 2. `defaultSelectedKeys` below is ONLY the "deselect-all" guard (what gets re-selected when
 *    the user unchecks every option). It intentionally does NOT influence what gets selected
 *    the first time a filter is added — that is out-of-the-box "select all" for every filter
 *    (see `resolveInitialSeedKeys` in `compound-filter.factory.ts`), unless a future entry here
 *    explicitly sets `initialSelectedKeys` to override it for a specific filter.
 *
 * A `groups` entry below only needs to describe the grouping it wants to CUSTOMIZE — it does
 * NOT need to enumerate every catalog compound filter for that key. Any catalog entry the BE
 * returns that isn't mentioned in `groups` ("orphans") is auto-appended by
 * `FilterOptionComponent.buildCompoundFilterGroups()` as its own single-item group, so it still
 * shows up (and stays selectable) without needing a hardcoded entry here.
 *
 * IMPORTANT: when a `groups` entry is set, `defaultSelectedKeys` (and `initialSelectedKeys`, if
 * used) MUST reference a key that actually appears among that same entry's `groups` items — NOT
 * the parent filter's own catalog key. A parent filter's own key is virtually never one of its
 * own compound-filter catalog keys (compound options live at dedicated 18xxx-range keys); a
 * `defaultSelectedKeys` that doesn't match anything in `groups` silently produces NO visibly
 * checked option when the user deselects everything (and an empty `compoundFilters: []` if that
 * state is ever committed) instead of the intended fallback. This exact mistake shipped for all
 * six `COMPANY_PARTY_GROUPS`-based Lease entries below (each set to its own key, e.g.
 * `LeaseColumns.Companies_NameCompany` = 1719, which is nowhere in `COMPANY_PARTY_GROUPS`) —
 * confirmed against a real BE payload and fixed by pointing them at `COMPANY_PARTY_DISPLAY_NAME_KEY`
 * (18877, "Display Name") instead, mirroring how Contact/Property already default to their own
 * "Display Name"-equivalent dedicated key. `compound-filter-groups.config.spec.ts` guards this
 * invariant going forward — see the exceptions list there for entries not yet confirmed.
 */

/** "Display Name" compound-filter catalog key shared by every Lease company-party role filter
 * (Tenant, Lessor, Sublessor, etc.) that uses `COMPANY_PARTY_GROUPS` — the natural default, since
 * it searches the same base field as the parent filter itself (confirmed against a real BE
 * payload for `LeaseColumns.Companies_NameCompany`). */
const COMPANY_PARTY_DISPLAY_NAME_KEY = 18877;

const COMPANY_ENTITY_NAME_GROUPS: CompoundFilterGroupDef[] = [
  {
    label: COMPANY_GROUP_LABEL,
    items: [{ key: 18889 }, { key: 18890 }, { key: 1301 }, { key: 1302 }],
  },
  { items: [{ key: 18763 }] },
  { items: [{ key: 18766 }] },
  { items: [{ key: 1304 }] },
];

const COMPANY_PARTY_GROUPS: CompoundFilterGroupDef[] = [
  {
    label: COMPANY_GROUP_LABEL,
    items: [
      { key: COMPANY_PARTY_DISPLAY_NAME_KEY }, // Display Name
      { key: 18815 },
      { key: 18874 },
      { key: 18875 },
      { key: 18878 },
      { key: 18880 },
      { key: 18879 },
    ],
  },
  { items: [{ key: 18768 }] },
  { items: [{ key: 18771 }] },
  { items: [{ key: 18876 }] },
];

const TENANT_NAICS_COMPOUND_GROUPS: CompoundFilterGroupDef[] = [
  { items: [{ key: 18882 }] },
  { items: [{ key: 18883 }] },
];

const CONTACT_COMPANY_NAME_COMPOUND_GROUPS: CompoundFilterGroupDef[] = [
  {
    label: COMPANY_GROUP_LABEL,
    items: [
      { key: 18891 }, // Display Name
      { key: 18892 }, // Company Name
      { key: 18893 }, // Legal Name
      { key: 18894 }, // Trade Style Name
    ],
  },
];

const PROPERTY_OWNER_COMPOUND_GROUPS: CompoundFilterGroupDef[] = [
  {
    label: COMPANY_GROUP_LABEL,
    items: [
      { key: 18897 }, // Display Name
      { key: 18898 }, // Company Name
      { key: 18899 }, // Trade Style Name
      { key: 18900 }, // Legal Name
    ],
  },
];

const AVAILABILITY_COMPANY_DEFAULT_KEY = 18903; // Display Name
const AVAILABILITY_COMPANY_NAME_COMPOUND_GROUPS_ITEMS: CompoundFilterGroupItem[] = [
  { key: 18903 }, // Display Name
  { key: 18904 }, // Company Name
  { key: 18905 }, // Trade Style Name
  { key: 18906 }, // Legal Name
  { key: 18908 }, // Original Name
];

const SALE_COMPANY_PARTY_GROUPS: CompoundFilterGroupDef[] = [
  {
    label: COMPANY_GROUP_LABEL,
    items: [
      { key: 18922 }, // Display Name
      { key: 18923 },
      { key: 18924 },
      { key: 18925 },
    ],
  },
  { items: [{ key: 18804 }] },
  { items: [{ key: 18801 }] },
  { items: [{ key: 16921 }] },
];

const PORTFOLIO_SALE__COMPANY_DEFAULT_KEY = 18928;
const PORTFOLIO_SALE_COMPANY_PARTY_GROUPS: CompoundFilterGroupItem[] = [
  { key: 18928 }, // Display Name
  { key: 18929 },
  { key: 18930 },
  { key: 18931 },
];

/** Property - NAICS Code filter key (no dedicated catalog enum member; matches the old config). */
const PROPERTY_NAICS_CODE_KEY = 422;
const PROPERTY_COMPANY_DEFAULT_KEY = 18897;
const COMPANY_ENTITY_DISPLAY_NAME_KEY = 18889;

export const COMPOUND_FILTER_CONFIGURATIONS: Record<number, CompoundFilterConfig> = {
  // ── Lease ────────────────────────────────────────────────────────────────
  [LeaseColumns.Companies_Tenant]: {
    defaultSelectedKeys: [COMPANY_PARTY_DISPLAY_NAME_KEY],
    groups: COMPANY_PARTY_GROUPS,
  },
  [LeaseColumns.Companies_TenantAgency]: {
    defaultSelectedKeys: [COMPANY_PARTY_DISPLAY_NAME_KEY],
    groups: COMPANY_PARTY_GROUPS,
  },
  [LeaseColumns.Companies_Lessor]: {
    defaultSelectedKeys: [COMPANY_PARTY_DISPLAY_NAME_KEY],
    groups: COMPANY_PARTY_GROUPS,
  },
  [LeaseColumns.Companies_ListingAgency]: {
    defaultSelectedKeys: [COMPANY_PARTY_DISPLAY_NAME_KEY],
    groups: COMPANY_PARTY_GROUPS,
  },
  [LeaseColumns.Companies_Sublessor]: {
    defaultSelectedKeys: [COMPANY_PARTY_DISPLAY_NAME_KEY],
    groups: COMPANY_PARTY_GROUPS,
  },
  [LeaseColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [COMPANY_PARTY_DISPLAY_NAME_KEY],
    groups: COMPANY_PARTY_GROUPS,
  },
  [LeaseColumns.LeaseCompDetails_TenantNAICSCode]: {
    defaultSelectedKeys: [18882],
    groups: TENANT_NAICS_COMPOUND_GROUPS,
  },

  // ── Company ──────────────────────────────────────────────────────────────
  [CompanyColumns.CompanyDetails_Name]: {
    defaultSelectedKeys: [COMPANY_ENTITY_DISPLAY_NAME_KEY],
    groups: COMPANY_ENTITY_NAME_GROUPS,
  },

  // ── Contact ──────────────────────────────────────────────────────────────
  [ContactColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [18891],
    groups: CONTACT_COMPANY_NAME_COMPOUND_GROUPS,
  },
  [ContactColumns.Companies_CompanyID]: {
    defaultSelectedKeys: [ContactColumns.Companies_CompanyID],
  },

  // ── Property ─────────────────────────────────────────────────────────────
  [PropertyColumns.Companies_Owner]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_TrueOwner]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_LegalOwner]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_ListingAgency]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_Developer]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_PropertyManager]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_Architect]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_AssetManager]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [PROPERTY_COMPANY_DEFAULT_KEY],
    groups: PROPERTY_OWNER_COMPOUND_GROUPS,
  },
  [PropertyColumns.Companies_CompanyID]: {
    defaultSelectedKeys: [18901],
  },
  [PROPERTY_NAICS_CODE_KEY]: {
    defaultSelectedKeys: [18919],
  },
  // ─────────────────────────────────────────────────────────
  // TIM
  // ─────────────────────────────────────────────────────────
  // 16274: TIM Contact & Contact Company Name
  [TimColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [18912], // Display Name,
    groups: [
      {
        label: COMPANY_GROUP_LABEL,
        items: [
          { key: 18912 }, // Display Name
          { key: 18913 }, // Company Name
          { key: 18914 }, // Trade Style Name
          { key: 18915 }, // Legal Name
          { key: 18916 }, // Original Name
        ],
      },
    ],
  },
  // 16178: TIM Company Name
  [TimColumns.TIMCompany_TIMCompanyName]: {
    defaultSelectedKeys: [18920], // Display Name,
    groups: [
      {
        label: COMPANY_GROUP_LABEL,
        items: [
          { key: 18920 }, // Display Name
          { key: 18921 }, // Company Name
          { key: 16179 }, // Trade Style Name
          { key: 16180 }, // Legal Name
        ],
      },
    ],
  },
  // 3740: TIM Tenant Agency
  [TimColumns.Companies_TenantAgency]: {
    defaultSelectedKeys: [18912], // Display Name,
    groups: [
      {
        label: COMPANY_GROUP_LABEL,
        items: [
          { key: 18912 }, // Display Name
          { key: 18913 }, // Company Name
          { key: 18914 }, // Trade Style Name
          { key: 18915 }, // Legal Name
        ],
      },
    ],
  },
  // Availability
  // ─────────────────────────────────────────────────────────
  // 501: Company Name
  [AvailabilityColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [AVAILABILITY_COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...AVAILABILITY_COMPANY_NAME_COMPOUND_GROUPS_ITEMS] }],
  },
  // 149: Prior Tenant
  [AvailabilityColumns.PriorTenant]: {
    defaultSelectedKeys: [AVAILABILITY_COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...AVAILABILITY_COMPANY_NAME_COMPOUND_GROUPS_ITEMS] }],
  },
  // 17120: Sublessor
  [AvailabilityColumns.Companies_Sublessor]: {
    defaultSelectedKeys: [AVAILABILITY_COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...AVAILABILITY_COMPANY_NAME_COMPOUND_GROUPS_ITEMS] }],
  },
  // 379: Listing Representative
  [AvailabilityColumns.ListingRep]: {
    defaultSelectedKeys: [AVAILABILITY_COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...AVAILABILITY_COMPANY_NAME_COMPOUND_GROUPS_ITEMS] }],
  },
  // 3737: Lessor
  [AvailabilityColumns.Companies_Lessor]: {
    defaultSelectedKeys: [AVAILABILITY_COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...AVAILABILITY_COMPANY_NAME_COMPOUND_GROUPS_ITEMS] }],
  },
  // ─────────────────────────────────────────────────────────
  // Sale
  // ─────────────────────────────────────────────────────────
  [SaleColumns.Companies_TrueSeller]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_LegalSeller]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_TrueBuyer]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_LegalBuyer]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_SellerAgency]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_BuyerAgency]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [18922],
    groups: SALE_COMPANY_PARTY_GROUPS,
  },
  [SaleColumns.Companies_CompanyID]: {
    defaultSelectedKeys: [3760],
  },
  [SaleColumns.Companies_NAICSCode]: {
    defaultSelectedKeys: [18927],
  },
  // ─────────────────────────────────────────────────────────
  // Portfolio Sale
  // ─────────────────────────────────────────────────────────

  [PortfoliosaleColumns.Companies_NameCompany]: {
    defaultSelectedKeys: [PORTFOLIO_SALE__COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...PORTFOLIO_SALE_COMPANY_PARTY_GROUPS] }],
  },
  [PortfoliosaleColumns.Companies_Seller]: {
    defaultSelectedKeys: [PORTFOLIO_SALE__COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...PORTFOLIO_SALE_COMPANY_PARTY_GROUPS] }],
  },
  [PortfoliosaleColumns.Companies_SellerAgency]: {
    defaultSelectedKeys: [PORTFOLIO_SALE__COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...PORTFOLIO_SALE_COMPANY_PARTY_GROUPS] }],
  },
  [PortfoliosaleColumns.Companies_Buyer]: {
    defaultSelectedKeys: [PORTFOLIO_SALE__COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...PORTFOLIO_SALE_COMPANY_PARTY_GROUPS] }],
  },
  [PortfoliosaleColumns.Companies_BuyerAgency]: {
    defaultSelectedKeys: [PORTFOLIO_SALE__COMPANY_DEFAULT_KEY],
    groups: [{ label: COMPANY_GROUP_LABEL, items: [...PORTFOLIO_SALE_COMPANY_PARTY_GROUPS] }],
  },

  [PortfoliosaleColumns.Companies_CompanyID]: {
    defaultSelectedKeys: [16970],
  },
  [PortfoliosaleColumns.Companies_NAICSCode]: {
    defaultSelectedKeys: [18963],
  },
};
